import { useReducer, useRef } from "react";
import data from "../../data/data.json";
// import trimmedData from "../../data/trimmed-data.json";

/** Defining the actions a user can take */
const ACTIONS = {
  BACK: "back",
  NEXT: "next",
  SELECT_ANSWER: "select-answer",
  SUBMIT: "submit",
};

export const SCORING = [
  { score: 2, value: "Strongly agree" },
  { score: 1, value: "Agree" },
  { score: 0, value: "Not sure" },
  { score: 0, value: "Disagree" },
  { score: 0, value: "Strongly disagree" },
];

export const TOTAL_SCORE = 96;

/** Controls the timeouts for when the graphs animate in */
export const TIMEOUTS = {
  1: 0,
  2: 400,
  3: 800,
  4: 1200,
  5: 1600,
  6: 2000,
};

export const DIMENSIONS = {
  CAPABILITIES: {
    color: "orange",
    label: "Capabilities",
    order: 4,
    timeout: TIMEOUTS[4],
  },
  CULTURE: {
    color: "red",
    label: "Culture",
    order: 2,
    timeout: TIMEOUTS[2],
  },
  CUSTOMER: {
    color: "green",
    label: "Customer",
    order: 1,
    timeout: TIMEOUTS[1],
  },
  DATA: {
    color: "softGray",
    label: "Data",
    order: 5,
    timeout: TIMEOUTS[5],
  },
  SUSTAINABLE_OPERATING_MODEL: {
    color: "teal",
    label: "Sustainable Operating Model",
    order: 3,
    timeout: TIMEOUTS[3],
  },
  TECHNOLOGY: {
    color: "purple",
    label: "Technology",
    order: 6,
    timeout: TIMEOUTS[6],
  },
};

export const MAXIMUM_SCORE = 25;

function reducer(state, action) {
  const { type, payload } = action;

  if (type === ACTIONS.SELECT_ANSWER) {
    const { question, answer } = payload;
    const indexOf = state.questions.findIndex((q) => q.id === question.id);
    const copiedQuestions = [...state.questions];

    copiedQuestions[indexOf].answer = answer;

    return {
      ...state,
      questions: copiedQuestions,
    };
  }

  if (type === ACTIONS.BACK) {
    const currentIndex = state.questions.findIndex(
      (q) => q.id === state.currentQuestion.id
    );

    if (currentIndex === 0) return state;

    const previousQuestion = state.questions[currentIndex - 1];

    return {
      ...state,
      currentQuestion: previousQuestion,
      nextHidden: false,
      /** The index of the *current* question, not the previous question that is now being selected */
      ...(currentIndex === 1 ? { backDisabled: true } : null),
    };
  }

  if (type === ACTIONS.NEXT) {
    const currentIndex = state.questions.findIndex(
      (q) => q.id === state.currentQuestion.id
    );

    if (currentIndex + 1 === state.questions.length)
      return {
        ...state,
        progress: 100,
      };

    const nextQuestion = state.questions[currentIndex + 1];

    let nextHidden = nextQuestion.answer === "";
    if (state.questions.length === currentIndex + 2) nextHidden = true;

    const currentProgress = ((currentIndex + 1) / state.questions.length) * 100;

    return {
      ...state,
      backDisabled: false,
      currentQuestion: nextQuestion,
      nextHidden,
      /** When the next question has an answer, there is no need to update progress. If it doesn't it means the next question is untouched and requires updating */
      progress: nextQuestion.answer !== "" ? state.progress : currentProgress,
    };
  }

  if (type === ACTIONS.SUBMIT) {
    const answeredQuestions = state.questions.reduce((acc, curr) => {
      const isValid = curr.answer !== "";
      return isValid ? acc + 1 : acc;
    }, 0);

    if (answeredQuestions === state.questions.length) {
      return {
        ...state,
        isComplete: true,
      };
    }
  }

  return state;
}

function useQuestions() {
  const [state, dispatch] = useReducer(reducer, {
    backDisabled: true,
    currentQuestion: data[0],
    isComplete: false,
    nextHidden: true,
    progress: 0,
    questions: data,
  });

  /** When a user clicks an answer, it will move them to the next question automatically with a timeout function. However, if they click another answer during that time, the timer needs to be updated to a new one. Using a ref, we can keep this value despite re-renders */
  const timeout = useRef();

  const handleSelectAnswer = ({ answer, questionId }) => {
    const question = state.questions.filter((q) => q.id === questionId)[0];
    if (timeout.current) clearTimeout(timeout.current);
    dispatch({
      payload: {
        answer,
        question,
      },
      type: ACTIONS.SELECT_ANSWER,
    });
    timeout.current = setTimeout(() => {
      dispatch({
        type: ACTIONS.NEXT,
      });
    }, 1000);
  };

  const handleNext = () => {
    dispatch({
      type: ACTIONS.NEXT,
    });
  };

  const handleBack = () => {
    dispatch({
      type: ACTIONS.BACK,
    });
  };

  const handleSubmit = () => {
    dispatch({
      type: ACTIONS.SUBMIT,
    });
  };

  const handlers = {
    handleBack,
    handleNext,
    handleSelectAnswer,
    handleSubmit,
  };

  return [state, handlers];
}

export default useQuestions;
